import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle, XCircle, Facebook } from 'lucide-react';
import { completeOAuth, requiresPageSelection } from '../../api/socialMedia';
import FacebookPagePickerModal from './components/FacebookPagePickerModal';

const SocialOAuthCallback = ({ platform: propPlatform }) => {
    const [status, setStatus] = useState('processing'); // processing, page_selection, success, error
    const [error, setError] = useState(null);
    const [platformName, setPlatformName] = useState('');

    // Facebook page selection state
    const [pageSelectionData, setPageSelectionData] = useState(null);

    useEffect(() => {
        const handleCallback = async () => {
            console.log("[SocialOAuthCallback] Starting OAuth callback handler...");
            console.log("[SocialOAuthCallback] URL:", window.location.href);

            try {
                // Parse URL parameters using native browser API
                const searchParams = new URLSearchParams(window.location.search);
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const errorParam = searchParams.get('error');

                console.log("[SocialOAuthCallback] Params:", { hasCode: !!code, hasState: !!state, error: errorParam });


                // Get platform from prop or URL path
                // URL format: /integrations/social/:platform (e.g., /integrations/social/facebook)
                let platform = propPlatform;
                if (!platform) {
                    const pathParts = window.location.pathname.split('/');
                    // Look for 'social' in path and get the next segment
                    const socialIndex = pathParts.indexOf('social');
                    if (socialIndex !== -1 && pathParts[socialIndex + 1]) {
                        platform = pathParts[socialIndex + 1];
                    }
                }

                setPlatformName(platform || 'Unknown');

                // Handle OAuth error
                if (errorParam) {
                    throw new Error(
                        searchParams.get('error_description') ||
                        `OAuth authorization was denied: ${errorParam}`
                    );
                }

                if (!code) {
                    throw new Error('No authorization code received');
                }

                if (!platform) {
                    throw new Error('Could not determine platform from URL');
                }

                // Note: We don't strictly enforce state validation on the frontend
                // because session storage may be lost after OAuth redirect.
                // The BACKEND validates the state token and extracts user identity from it.
                const storedState = sessionStorage.getItem('social_oauth_state');
                if (storedState && state !== storedState) {
                    console.warn("[SocialOAuthCallback] State mismatch - backend will validate");
                }
                if (!storedState) {
                    console.log("[SocialOAuthCallback] No stored state - normal after redirect, backend will validate");
                }

                console.log("[SocialOAuthCallback] Calling completeOAuth for", platform);

                // Complete OAuth flow - NO JWT AUTH REQUIRED
                // Backend extracts user identity from the state token
                const response = await completeOAuth(platform, code, state);
                console.log("[SocialOAuthCallback] OAuth response:", response);

                // Check if Facebook requires page selection
                // Backend returns camelCase: requiresSelection, oauthState
                if (response && response.requiresSelection === true) {
                    console.log("[SocialOAuthCallback] Facebook requires page selection, pages:", response.pages?.length);
                    setPageSelectionData({
                        pages: response.pages || [],
                        oauthState: response.oauthState,
                    });
                    setStatus('page_selection');
                    return;
                }

                // Clean up session storage
                sessionStorage.removeItem('social_oauth_state');
                sessionStorage.removeItem('social_oauth_platform');

                // Store a flag to indicate successful connection for the marketing page
                sessionStorage.setItem('social_oauth_success', platform);

                setStatus('success');

                // DEBUG: Don't auto-redirect - let user click to continue
                console.log('[SocialOAuthCallback] SUCCESS - Click button to continue');
                // setTimeout(() => {
                //     window.location.href = '/?view=marketing';
                // }, 2000);
            } catch (err) {
                console.error('OAuth callback error:', err);
                setError(err.message || 'Failed to complete connection');
                setStatus('error');

                // Clean up session storage on error too
                sessionStorage.removeItem('social_oauth_state');
                sessionStorage.removeItem('social_oauth_platform');
            }
        };

        handleCallback();
    }, [propPlatform]);

    // Handle successful page selection
    const handlePageSelected = (integration) => {
        console.log('[SocialOAuthCallback] Page selected successfully:', integration);

        // Clean up session storage
        sessionStorage.removeItem('social_oauth_state');
        sessionStorage.removeItem('social_oauth_platform');

        // Store success flag with the account name
        sessionStorage.setItem('social_oauth_success', 'facebook');
        sessionStorage.setItem('social_oauth_account_name', integration.accountName || integration.account_name || '');

        setStatus('success');

        // DEBUG: Don't auto-redirect - let user click to continue
        console.log('[SocialOAuthCallback] SUCCESS - Click button to continue');
        // setTimeout(() => {
        //     window.location.href = '/?view=marketing';
        // }, 2000);
    };

    // Handle page selection cancellation
    const handlePageSelectionCancel = () => {
        // Clean up session storage
        sessionStorage.removeItem('social_oauth_state');
        sessionStorage.removeItem('social_oauth_platform');

        setError('Facebook page selection was cancelled');
        setStatus('error');
        setPageSelectionData(null);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
            {/* Facebook Page Picker Modal */}
            {status === 'page_selection' && pageSelectionData && (
                <FacebookPagePickerModal
                    isOpen={true}
                    pages={pageSelectionData.pages}
                    oauthState={pageSelectionData.oauthState}
                    onSuccess={handlePageSelected}
                    onCancel={handlePageSelectionCancel}
                />
            )}

            {/* Status Cards */}
            <div className="max-w-md w-full p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-xl text-center">
                {status === 'processing' && (
                    <>
                        <Loader2 className="w-16 h-16 mx-auto mb-6 text-sky-500 animate-spin" />
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                            Connecting Account
                        </h1>
                        <p className="text-slate-600 dark:text-slate-400">
                            Please wait while we complete the connection...
                        </p>
                    </>
                )}

                {status === 'page_selection' && (
                    <>
                        <Facebook className="w-16 h-16 mx-auto mb-6 text-blue-500" />
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                            Select Your Page
                        </h1>
                        <p className="text-slate-600 dark:text-slate-400">
                            Choose which Facebook Page you want to connect
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircle className="w-16 h-16 mx-auto mb-6 text-emerald-500" />
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                            Successfully Connected!
                        </h1>
                        <p className="text-slate-600 dark:text-slate-400 mb-6">
                            Your {platformName} account has been connected.
                        </p>
                        <button
                            onClick={() => { window.location.href = '/?view=marketing'; }}
                            className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                        >
                            Continue to Marketing
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <XCircle className="w-16 h-16 mx-auto mb-6 text-red-500" />
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                            Connection Failed
                        </h1>
                        <p className="text-slate-600 dark:text-slate-400 mb-6">
                            {error}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={() => { window.location.href = '/?view=marketing'; }}
                                className="px-6 py-2.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            >
                                Go Back
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-2.5 rounded-lg bg-sky-600 text-white hover:bg-sky-700 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SocialOAuthCallback;
